import { ReportType } from '@/lib/api/reports/reports-api'
import { useAuth } from '@/store/hooks'
import { useAppSelector } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdAssessment, MdBusiness, MdFactCheck, MdHub, MdScheduleSend, MdTrendingUp } from 'react-icons/md'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import { useAppDispatch } from '@/store/hooks'
import { setAccessibleCompanies } from '@/store/slices/authSlice'
import MonthClosingSection from './components/month-closing-section'
import ProductivitySection from './components/productivity-section'
import ReportSection from './components/report-section'
import {
    BILLING_STATUS_LABELS,
    BILLING_STATUS_NONE,
    BILLING_STATUS_NONE_LABEL,
    getReportConfigs,
    REPORT_RESOURCE,
} from './config/report-configs'
import { useResourceVisibility } from '@/lib/hooks/use-entity-access'
import {
    readDefaultBillingStatus,
    saveDefaultBillingStatus,
} from './config/default-billing-status'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

/**
 * `productivity` não é um tipo de relatório do backend: é uma visão composta sobre
 * consultas/exames/procedimentos filtrados por quem os criou.
 */
const PRODUCTIVITY_TAB = 'productivity'

/**
 * Fechamento do mês: o detalhamento da tabela "O que foi vendido" do dashboard
 * financeiro, com os Pix sem vínculo à parte. Também não é tipo de relatório do backend
 * (vem de `financial/dashboard/month-closing`).
 */
const MONTH_CLOSING_TAB = 'month-closing'

type TabKey = ReportType | typeof PRODUCTIVITY_TAB | typeof MONTH_CLOSING_TAB

interface TabDef {
    key: TabKey
    healthOnly?: boolean
    nonHealthOnly?: boolean
}

const TABS: TabDef[] = [
    { key: 'consultations' },
    { key: 'orders' },
    { key: 'exams' },
    { key: 'billings' },
    { key: 'clients' },
    // Atendimentos, Leads, Negócios e Funil saíram daqui (o dashboard de marketing cobre os
    // três últimos). As configs (`report-configs.ts`) e o backend continuam, para as
    // automações já criadas.
    { key: PRODUCTIVITY_TAB },
    { key: MONTH_CLOSING_TAB },
]

const PRODUCTIVITY_TAB_CONFIG = {
    label: 'Produtividade',
    icon: MdTrendingUp,
}

const MONTH_CLOSING_TAB_CONFIG = {
    label: 'Fechamento do mês',
    icon: MdFactCheck,
}

export default function DataReportsPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const accessibleCompanies = useAppSelector(s => s.auth.accessibleCompanies)
    const segment = user?.company?.segment || 'OTHER'

    const isResourceVisible = useResourceVisibility()
    const isReportAllowed = (key: string): boolean => {
        const required = REPORT_RESOURCE[key]
        return !required || isResourceVisible(required.module, required.resource)
    }
    // Produtividade é uma visão sobre consultas, exames e pedidos: existe se ao
    // menos um deles estiver liberado.
    const canSeeProductivity = ['consultations', 'exams', 'orders'].some(
        isReportAllowed
    )

    const visibleTabs = TABS.filter(
        (tab) =>
            (!tab.healthOnly || segment === 'HEALTH') &&
            (!tab.nonHealthOnly || segment !== 'HEALTH') &&
            (tab.key === PRODUCTIVITY_TAB || tab.key === MONTH_CLOSING_TAB
                ? canSeeProductivity
                : isReportAllowed(tab.key))
    )

    const [selectedTab, setActiveTab] = useState<TabKey>('consultations')
    // A aba padrão (Consultas) pode não estar liberada: cai na primeira visível.
    const activeTab: TabKey = visibleTabs.some(tab => tab.key === selectedTab)
        ? selectedTab
        : (visibleTabs[0]?.key ?? selectedTab)
    const [scope, setScope] = useState<'company' | 'organization'>('company')
    const [companyId, setCompanyId] = useState<number | undefined>(undefined)
    /**
     * Status da cobrança com que os relatórios abrem. Lido do navegador na montagem —
     * a preferência é de quem opera, não do sistema (ver `default-billing-status.ts`).
     */
    const [defaultBillingStatus, setDefaultBillingStatus] = useState(
        readDefaultBillingStatus
    )

    // Carregar empresas acessíveis pra dropdown
    useEffect(() => {
        if (accessibleCompanies.length === 0) {
            getAccessibleCompanies()
                .then(res => {
                    if (res.success && res.result) {
                        dispatch(setAccessibleCompanies(res.result))
                    }
                })
                .catch(() => { })
        }
    }, [accessibleCompanies.length, dispatch])

    const serviceLabels = useServiceLabel()
    const reportConfigs = useMemo(
        () => getReportConfigs(serviceLabels, segment),
        [serviceLabels, segment]
    )
    const isProductivity = activeTab === PRODUCTIVITY_TAB
    const isMonthClosing = activeTab === MONTH_CLOSING_TAB
    const activeConfig =
        isProductivity || isMonthClosing ? null : reportConfigs[activeTab as ReportType]

    const scopeFilter = useMemo(() => {
        if (scope === 'organization') return { scope: 'organization' as const }
        if (companyId) return { companyId }
        return {}
    }, [scope, companyId])

    // O padrão entra na key: mudá-lo remonta a seção, que recarrega já com o novo
    // recorte. Sem isso a escolha só valeria na próxima visita à tela.
    const scopeKey = `${scope}-${companyId ?? 'current'}-${defaultBillingStatus || 'all'}`

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center">
                        <MdAssessment className="text-2xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Relatórios
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Visualize dados consolidados e exporte relatórios
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost border border-base-300 gap-2"
                        onClick={() => navigate('/core/reports/automations')}
                    >
                        <MdScheduleSend />
                        Automações
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className={`btn btn-sm gap-2 ${scope === 'company' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => setScope('company')}
                        >
                            <MdBusiness />
                            Empresa
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm gap-2 ${scope === 'organization' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => {
                                setScope('organization')
                                setCompanyId(undefined)
                            }}
                        >
                            <MdHub />
                            Todas filiais
                        </button>
                    </div>

                    {/* Recorte com que os relatórios já abrem. O financeiro trabalha
                        quase sempre em "Pago", e isso morava atrás de "Mais filtros" —
                        remarcado a cada aba e a cada recarga. */}
                    {/* O fechamento não tem filtro de status nem de outra filial: o recorte é o
                        da tabela do dashboard, e só "Empresa" (filial atual) ou "Todas filiais". */}
                    {!isMonthClosing && (
                    <select
                        // Largura fixa e curta: a barra já tem quatro controles e
                        // quebrava para a segunda linha. O que o controle É fica no
                        // `title` e no rótulo curto, não no texto de cada opção.
                        className="select select-bordered select-sm w-[150px]"
                        value={defaultBillingStatus}
                        title="Status da cobrança aplicado ao abrir os relatórios que têm esse filtro"
                        onChange={e => {
                            const value = e.target.value
                            setDefaultBillingStatus(value)
                            saveDefaultBillingStatus(value)
                        }}
                    >
                        <option value="">Status: todos</option>
                        {Object.entries(BILLING_STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                        <option value={BILLING_STATUS_NONE}>
                            {BILLING_STATUS_NONE_LABEL}
                        </option>
                    </select>
                    )}

                    {!isMonthClosing && scope === 'company' && accessibleCompanies.length > 1 && (
                        <select
                            className="select select-bordered select-sm w-[170px]"
                            value={companyId ?? ''}
                            title="Filial dos dados"
                            onChange={e => {
                                const v = e.target.value
                                setCompanyId(v ? Number(v) : undefined)
                            }}
                        >
                            <option value="">
                                Atual: {user?.company?.tradingName || user?.company?.name || 'Empresa'}
                            </option>
                            {accessibleCompanies.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.tradingName || c.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 p-2">
                <div className="flex flex-wrap gap-1">
                    {visibleTabs.map((tab) => {
                        const cfg =
                            tab.key === PRODUCTIVITY_TAB
                                ? PRODUCTIVITY_TAB_CONFIG
                                : tab.key === MONTH_CLOSING_TAB
                                  ? MONTH_CLOSING_TAB_CONFIG
                                  : reportConfigs[tab.key]
                        if (!cfg) return null
                        const TabIcon = cfg.icon
                        const isActive = activeTab === tab.key
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                className={`flex items-center gap-1.5 px-2.5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${isActive
                                        ? 'bg-secondary text-primary-content shadow-sm'
                                        : 'text-base-content/70 hover:bg-base-200'
                                    }`}
                                onClick={() => {
                                    setActiveTab(tab.key)
                                    // O fechamento confere com o dashboard, que abre em todas
                                    // as filiais: entra no mesmo recorte.
                                    if (tab.key === MONTH_CLOSING_TAB) {
                                        setScope('organization')
                                        setCompanyId(undefined)
                                    }
                                }}
                            >
                                {TabIcon && <TabIcon className="text-lg" />}
                                {cfg.label}
                            </button>
                        )
                    })}
                </div>
            </div>

            {isProductivity && (
                <ProductivitySection
                    key={`${activeTab}-${scopeKey}`}
                    scopeFilter={scopeFilter}
                    defaultBillingStatus={defaultBillingStatus}
                />
            )}

            {isMonthClosing && <MonthClosingSection scope={scope} />}

            {!isProductivity && !isMonthClosing && activeConfig && (
                <ReportSection
                    key={`${activeTab}-${scopeKey}`}
                    reportType={activeTab}
                    config={activeConfig}
                    scopeFilter={scopeFilter}
                    defaultBillingStatus={defaultBillingStatus}
                />
            )}
        </div>
    )
}
